// The IDE's state, written at the start of each of the user's messages to
// Blip (docs/BLIP_HELPER_DESIGN.md, "Context"), in an <ide> block that the
// system prompt says the IDE writes, not the user. Blip reads files through
// his tools, so only their names and sizes are here.

const FILES_SHOWN = 60;

const size = (bytes) => bytes < 1024 ? `${bytes} bytes` : `${Math.round(bytes / 1024)} KB`;

// ide: { project(), editor, isText(path), mode(), lastRun(), running(), worldEditor(), buddies() }
// worldLine: what the open World Editor says of its world (blip/world.js), or null.
// modelsLine: whether models can be made, and the energy left (panel.js).
export function ideContext(ide, worldLine = null, modelsLine = null, extra = null) {
  const project = ide.project();
  const lines = [];
  const mode = ide.mode() === "original" ? "Original" : "Extended";
  lines.push(`Project: ${project.name}, ${mode} mode, main file ${project.main || "not chosen yet"}`);
  // Which IDE: the system prompt describes the online one. Only the
  // Windows desktop IDE runs .ui forms (forms.md).
  if (ide.desktop) {
    const platform = ide.platform && ide.platform();
    lines.push(`IDE: the desktop IDE${platform ? ` on ${platform}` : ""}, where the user runs programs with Run, in a window of their own`);
    if (project.protected) {
      lines.push("This project is one of the installed examples, which stay as they are: Run runs a working copy, and you " +
        "can read the files but not change them or add any. To build on it, the user makes a project of their own " +
        "(File > New Project), and you write it there.");
    }
  }
  // The TODO notes (todo.js) are one entry, so that they never push the
  // project's own files out of those shown.
  const notes = project.paths().filter((path) => /^todo\//i.test(path));
  const paths = project.paths().filter((path) => !/^todo\//i.test(path));
  const shown = paths.slice(0, FILES_SHOWN).map((path) => {
    const blob = project.blob(path);
    const kind = ide.isText(path) ? "" : /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(path) ? "picture, " :
      /\.(wav|ogg|mp3|flac|m4a)$/i.test(path) ? "sound, " : "";
    return `${path} (${kind}${size(blob ? blob.size : 0)})`;
  });
  if (notes.length) {
    const count = notes.filter((path) => /^todo\/[^/]+\.md$/i.test(path) && !/^todo\/readme\.md$/i.test(path)).length;
    const pictures = notes.filter((path) => /^todo\/pictures\//i.test(path)).length;
    // In the desktop IDE, notes may be kept in a folder of the user's own,
    // which the IDE maps todo/ to.
    const kept = ide.todoFolder && ide.todoFolder() ? `, kept in ${ide.todoFolder()}` : "";
    shown.push(`todo/ (TODO notes${kept}: ${count} ${count === 1 ? "note" : "notes"}, ${pictures} ${pictures === 1 ? "picture" :
      "pictures"})`);
  }
  lines.push(`Files: ${shown.join(", ") || "none"}${paths.length > FILES_SHOWN ? `, and ${paths.length - FILES_SHOWN} more` : ""}`);
  const editor = ide.editor;
  // The desktop IDE says itself what its editor shows (desktop.js).
  if (editor.cursorText) {
    lines.push(editor.cursorText());
  } else if (editor.path && project.has(editor.path)) {
    const state = editor.view.state;
    const range = state.selection.main;
    const head = state.doc.lineAt(range.head);
    let open = `Open in the editor: ${editor.path}, cursor on row ${head.number}, column ${range.head - head.from + 1}`;
    if (!range.empty) {
      const from = state.doc.lineAt(range.from).number, to = state.doc.lineAt(range.to).number;
      open += from === to ? `, part of row ${from} selected` : `, rows ${from} to ${to} selected`;
    }
    lines.push(open);
  } else {
    lines.push("Open in the editor: no file (the help is showing)");
  }
  // A buddy session (buddies/session.js): others change the files too.
  const session = ide.buddies && ide.buddies();
  if (session && session.you) {
    const others = [...new Set(session.people.filter((person) => person.id !== session.you.id).map((person) => person.name))];
    lines.push(`A buddy session is open${others.length ? ` with ${others.join(", ")}` : ""}: others may change the ` +
      "project's files while you work, so read a file again just before you change it.");
  }
  const last = ide.lastRun();
  const world = ide.worldEditor();
  if (world && worldLine) {
    lines.push(worldLine);
  } else if (world) {
    lines.push(`The World Editor is open, in the program's place, on ${world.path || "a new world"}. A run would ` +
      "close it, so run_program only compiles until the user closes it.");
  } else if (ide.running()) {
    lines.push("A program is running now.");
  } else if (last && last.ended) {
    const ended = last.ended;
    if (ended.status === "compile-error") {
      const errors = (ended.errors || []).slice(0, 5).map((error) => `${error.file}:${error.row}:${error.col} ${error.message}`);
      lines.push(`Last build: ${errors.length ? `errors: ${errors.join("; ")}` : "could not compile"}`);
    } else if (ended.status === "no-main") {
      lines.push("Last build: no main file chosen");
    } else if (ended.status === 0) {
      lines.push(`Last run${last.check ? " (Blip's test)" : ""}: ended normally`);
    } else if (typeof ended.status === "number") {
      const where = ended.location ? ` at ${ended.location.file} row ${ended.location.line}` : "";
      lines.push(`Last run${last.check ? " (Blip's test)" : ""}: stopped with a runtime error${where}: ${ended.message || ended.status}`);
    }
  }
  if (!navigator.gpu && !ide.desktop) lines.push("This browser has no WebGPU, so programs run without 3D graphics.");
  if (modelsLine) lines.push(modelsLine);
  if (extra) lines.push(extra);
  return `<ide>\n${lines.join("\n")}\n</ide>`;
}
